import React, { useEffect, useRef, useState } from "react";
import { Trophy, RefreshCw, Send, EyeOff, Image as ImageIcon, X, Pin, PinOff, Pencil, Trash2, ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import ConfirmDialog from "../components/ConfirmDialog.jsx";

// Deník úspěchů v adminu (15.7.2026): tým vidí feed z klientského portálu,
// reaguje, komentuje (klientkám se ukáže se štítkem TRENÉR) a píše vlastní
// příspěvky (štítek FITWAY40). Data: /api/denik-admin.

const EMOJI = ["❤️", "👏", "🔥", "💪", "🙌", "😍"];
const EMOJI_PICKER = ["❤️", "👏", "🔥", "💪", "🙌", "😍", "👍", "🎉", "✨", "😊", "🥳", "💯", "🙏", "😂", "💥", "🏆", "👌", "🤩", "😅", "🤗"];

// HEIC podpora (iPhone fotky). Prohlizece (mimo Safari) HEIC nedekoduji, proto
// pri HEIC souboru lazy-loadneme heic2any z cdnjs (CSP ho povoluje) a prevedeme
// na JPEG jeste pred zmensenim. Promise cachujeme, at se skript nacte jen jednou.
let heicLibPromise = null;
function loadHeic2any() {
  if (typeof window !== "undefined" && window.heic2any) return Promise.resolve(window.heic2any);
  if (heicLibPromise) return heicLibPromise;
  heicLibPromise = new Promise((resolve, reject) => {
    const sc = document.createElement("script");
    sc.src = "https://cdnjs.cloudflare.com/ajax/libs/heic2any/0.0.4/heic2any.min.js";
    sc.async = true;
    sc.onload = () => resolve(window.heic2any);
    sc.onerror = () => { heicLibPromise = null; reject(new Error("heic2any se nepodařilo načíst")); };
    document.head.appendChild(sc);
  });
  return heicLibPromise;
}
const jeHeic = (file) => {
  const t = (file.type || "").toLowerCase();
  const n = (file.name || "").toLowerCase();
  return t === "image/heic" || t === "image/heif" || n.endsWith(".heic") || n.endsWith(".heif");
};

function fmtDT(d) {
  try {
    const x = new Date(d);
    return `${x.toLocaleDateString("cs-CZ")} ${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
}

// Základní formátování v textu deníku (15.7.2026): **tučně**, *kurzíva*,
// __podtržení__. Bezpečné — parsuje se na React elementy, žádné HTML.
// Stejná syntaxe se vykresluje i v klientské aplikaci.
function fmtText(text) {
  const s = String(text == null ? "" : text);
  const out = [];
  const re = /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[2] != null) out.push(<b key={i++}>{m[2]}</b>);
    else if (m[3] != null) out.push(<u key={i++}>{m[3]}</u>);
    else if (m[4] != null) out.push(<i key={i++}>{m[4]}</i>);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

function Avatar({ foto, jmeno, tym, size = 38 }) {
  if (foto) return <img src={foto} alt="" style={{ width: size, height: size }} className="rounded-full object-cover flex-shrink-0" />;
  const ini = (String(jmeno || "?").trim().split(/\s+/).map((x) => x[0]).slice(0, 2).join("") || "?").toUpperCase();
  return (
    <span style={{ width: size, height: size, fontSize: Math.round(size / 2.9) }}
      className={`rounded-full flex items-center justify-center font-extrabold flex-shrink-0 ${tym ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
      {ini}
    </span>
  );
}

// Cache posledního feedu — deník se po znovuotevření ukáže OKAMŽITĚ, refetch běží na pozadí.
let _denikCache = null; // { posts, celkem, mojeFoto }

// Ikonové tlačítko v hlavičce (mobil: 44 px s rámečkem, desktop: jen ikonka).
const IKONA_TLACITKO = "flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-xl border border-brand-200 text-brand-700 transition active:bg-brand-50 sm:min-h-0 sm:min-w-0 sm:rounded-lg sm:border-0 sm:p-1.5 sm:text-slate-400 sm:hover:bg-brand-50 sm:hover:text-brand-700";

export default function Denik() {
  const user = getCurrentUser();
  // 2.9.2026: uvnitř trenérské sekce (/trener/denik) má stránka spodní menu trenéra,
  // kompaktní hlavičku s tlačítkem Zpět a bez vlastního odsazení (dává ho TrenerLayout).
  const location = useLocation();
  const navigate = useNavigate();
  const vTrenerovi = location.pathname.startsWith("/trener/");
  const zpet = () => { if (window.history.length > 1) navigate(-1); else navigate("/trener/dashboard"); };
  // Trenér na starém odkazu /denik (notifikace, záložka) → do své sekce.
  useEffect(() => { if (!vTrenerovi && String(user?.role || "") === "trener") navigate("/trener/denik", { replace: true }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const canHide = ["owner", "manager"].includes(user?.role || "");
  const canPin = ["owner", "csm"].includes(user?.role || "");
  const canHideMod = ["owner", "manager", "csm"].includes(user?.role || "");   // skrýt/archivovat cokoliv
  const canDeleteMod = ["owner", "manager"].includes(user?.role || "");        // trvale smazat cokoliv
  const [posts, setPosts] = useState(_denikCache?.posts || null);
  const [celkem, setCelkem] = useState(_denikCache?.celkem || 0);
  const [mojeFoto, setMojeFoto] = useState(_denikCache?.mojeFoto || null); // fotka přihlášeného (optimistika)
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [novy, setNovy] = useState("");
  const [novaFotka, setNovaFotka] = useState(null); // data URL ≤400 KB
  const [fotoBusy, setFotoBusy] = useState(false); // probiha zpracovani/HEIC konverze
  const fotkaRef = useRef(null);
  const [komText, setKomText] = useState({}); // id → rozepsaný komentář
  const [emojiOpen, setEmojiOpen] = useState(false); // emoji picker v composeru
  const [komEmojiOpen, setKomEmojiOpen] = useState(null); // komentarId s otevřeným výběrem reakce
  const [editKom, setEditKom] = useState(null); // komentarId, který upravuji
  const [editText, setEditText] = useState({}); // komentarId → upravený text
  const [confirmState, setConfirmState] = useState(null);

  const load = async (offset = 0) => {
    setErr("");
    try {
      const r = await apiFetch(`/api/denik-admin?offset=${offset}`);
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Deník se nepodařilo načíst"); return; }
      setCelkem(j.celkem);
      if (offset === 0 && j.mojeFoto !== undefined) setMojeFoto(j.mojeFoto);
      setPosts((p) => {
        const next = offset === 0 ? j.posts : [...(p || []), ...j.posts];
        if (offset === 0) _denikCache = { posts: j.posts, celkem: j.celkem, mojeFoto: j.mojeFoto ?? null };
        return next;
      });
    } catch (e) { setErr(e.message || "Deník se nepodařilo načíst"); }
  };
  useEffect(() => { load(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (body) => {
    setErr("");
    const r = await apiFetch("/api/denik-admin", { method: "POST", body: JSON.stringify(body) });
    const j = await r.json();
    if (!j.ok) { setErr(j.error || "Akce se nepovedla"); return null; }
    return j;
  };

  const reaguj = async (p, emoji) => {
    // Optimisticky přepnout, server je zdroj pravdy při dalším načtení.
    setPosts((arr) => (arr || []).map((x) => {
      if (x.id !== p.id) return x;
      const moje = x.mojeReakce || [];
      const uz = moje.includes(emoji);
      const reakce = { ...x.reakce, [emoji]: Math.max(0, (x.reakce?.[emoji] || 0) + (uz ? -1 : 1)) };
      if (!reakce[emoji]) delete reakce[emoji];
      return { ...x, reakce, mojeReakce: uz ? moje.filter((e) => e !== emoji) : [...moje, emoji] };
    }));
    await post({ action: "react", prispevek_id: p.id, emoji });
  };

  const komentuj = async (p) => {
    const text = String(komText[p.id] || "").trim();
    if (!text) return;
    setKomText((s) => ({ ...s, [p.id]: "" }));
    const tmpId = "tmp-" + Date.now();
    setPosts((arr) => (arr || []).map((x) => x.id === p.id
      ? { ...x, komentare: [...(x.komentare || []), { id: tmpId, parentId: null, reakce: {}, mojeReakce: [], moje: true, autor: user?.name || "Já", trener: true, text, cas: new Date().toISOString(), foto: mojeFoto }] }
      : x));
    const j = await post({ action: "comment", prispevek_id: p.id, text });
    if (j && j.id) setPosts((arr) => (arr || []).map((x) => x.id === p.id
      ? { ...x, komentare: (x.komentare || []).map((c) => c.id === tmpId ? { ...c, id: j.id } : c) } : x));
  };

  // Reakce na komentář (toggle, optimisticky).
  const reagujKomentar = async (p, k, emoji) => {
    if (String(k.id || "").startsWith("tmp-")) return; // ještě neuložený komentář
    setPosts((arr) => (arr || []).map((x) => {
      if (x.id !== p.id) return x;
      return { ...x, komentare: (x.komentare || []).map((c) => {
        if (c.id !== k.id) return c;
        const moje = c.mojeReakce || [];
        const uz = moje.includes(emoji);
        const reakce = { ...(c.reakce || {}), [emoji]: Math.max(0, ((c.reakce && c.reakce[emoji]) || 0) + (uz ? -1 : 1)) };
        if (!reakce[emoji]) delete reakce[emoji];
        return { ...c, reakce, mojeReakce: uz ? moje.filter((e) => e !== emoji) : [...moje, emoji] };
      }) };
    }));
    await post({ action: "comment-react", komentar_id: k.id, emoji });
  };


  const CAN_EDIT_MS = 60 * 60 * 1000; // 60 minut od odeslání
  const muzuUpravit = (k) => !!k.moje && !String(k.id || "").startsWith("tmp-") && (Date.now() - new Date(k.cas).getTime() < CAN_EDIT_MS);

  // Úprava vlastního komentáře do 60 min.
  const ulozitEdit = async (p, k) => {
    const text = String(editText[k.id] ?? k.text).trim();
    if (!text) return;
    setEditKom(null);
    setPosts((arr) => (arr || []).map((x) => x.id !== p.id ? x
      : { ...x, komentare: (x.komentare || []).map((c) => c.id === k.id ? { ...c, text, edited: true } : c) }));
    const j = await post({ action: "comment-edit", komentar_id: k.id, text });
    if (!j) load(0);
  };

  // Smazání vlastního komentáře do 60 min (uklidí i rozepsanou odpověď/úpravu).
  const smazatKomentar = (p, k) => {
    if (String(k.id || "").startsWith("tmp-")) return;
    setConfirmState({
      title: "Smazat tento komentář?",
      description: "Komentář se odstraní z feedu.",
      confirmLabel: "Smazat",
      onConfirm: async () => {
        setConfirmState(null);
        if (editKom === k.id) setEditKom(null);
        setPosts((arr) => (arr || []).map((x) => {
          if (x.id !== p.id) return x;
          const kept = (x.komentare || []).filter((c) => c.id !== k.id).map((c) => c.parentId === k.id ? { ...c, parentId: null } : c);
          return { ...x, komentare: kept };
        }));
        const j = await post({ action: "comment-delete", komentar_id: k.id });
        if (!j) load(0);
      },
    });
  };

  // Vykreslení jednoho komentáře (isReply = odsazená odpověď).
  const renderComment = (p, k, isReply) => {
    const rk = k.reakce || {};
    const active = Object.keys(rk).filter((e) => (rk[e] || 0) > 0);
    return (
      <div key={k.id} className={`flex items-start gap-2 ${isReply ? "ml-8" : ""}`}>
        <Avatar foto={k.foto} jmeno={k.autor} tym={k.trener} size={isReply ? 28 : 34} />
        <div className="min-w-0 flex-1">
          {/* 3.9.2026: autor a čas jako pilulky, ať se to čte stejně jako štítky jinde. */}
          <span className="flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${k.trener ? "bg-sky-50 text-sky-800" : "bg-slate-100 text-slate-700"}`}>
              {k.autor}
            </span>
            {k.trener && <span className="inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold text-sky-700">TRENÉR</span>}
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold tabular-nums text-slate-600">{fmtDT(k.cas)}</span>
            {k.edited && <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">upraveno</span>}
          </span>
          {editKom === k.id ? (
            <div className="flex items-center gap-2 mt-1">
              <input value={editText[k.id] ?? k.text} onChange={(e) => setEditText((sx) => ({ ...sx, [k.id]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") ulozitEdit(p, k); if (e.key === "Escape") setEditKom(null); }} autoFocus maxLength={300}
                className="field flex-1" />
              <button onClick={() => ulozitEdit(p, k)} className="text-sm font-semibold text-sky-700 hover:text-sky-900 px-1">Uložit</button>
              <button onClick={() => setEditKom(null)} className="text-sm text-slate-400 hover:text-slate-600 px-1">Zrušit</button>
            </div>
          ) : (
            <div className="text-sm leading-relaxed text-slate-600 whitespace-pre-wrap">{fmtText(k.text)}</div>
          )}
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {active.map((e) => {
              const moje = (k.mojeReakce || []).includes(e);
              return (
                <button key={e} onClick={() => reagujKomentar(p, k, e)}
                  className={`text-xs rounded-full px-2 py-0.5 border transition-colors ${moje ? "bg-emerald-50 border-emerald-300 font-semibold" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`}>
                  {e} {rk[e]}
                </button>
              );
            })}
            <button onClick={() => setKomEmojiOpen((v) => (v === k.id ? null : k.id))} title="Přidat emoji reakci"
              className="text-[11px] font-semibold rounded-full px-2 py-0.5 border border-slate-200 bg-slate-50 hover:border-emerald-300 hover:bg-emerald-50 text-slate-500 inline-flex items-center gap-1">😊 Reagovat</button>
            {(muzuUpravit(k) || canHideMod || canDeleteMod) && !String(k.id || "").startsWith("tmp-") && (
              <span className="ml-auto flex items-center gap-1.5">
                {muzuUpravit(k) && (
                  <button onClick={() => { setEditKom(k.id); setEditText((sx) => ({ ...sx, [k.id]: k.text })); }}
                    title="Upravit" className="text-slate-400 hover:text-sky-600 p-1"><Pencil className="w-4 h-4" /></button>
                )}
                {canHideMod && (
                  <button onClick={() => skrytKomentar(p, k)}
                    title="Skrýt / archivovat komentář (moderace)" className="text-slate-400 hover:text-amber-600 p-1"><EyeOff className="w-4 h-4" /></button>
                )}
                {(muzuUpravit(k) || canDeleteMod) && (
                  <button onClick={() => smazatKomentar(p, k)}
                    title={canDeleteMod ? "Trvale smazat komentář (moderace)" : "Smazat"} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                )}
              </span>
            )}
          </div>
          {komEmojiOpen === k.id && (
            <div className="flex gap-1 mt-1 p-1.5 border border-slate-200 rounded-xl bg-white w-fit shadow-sm">
              {EMOJI.map((e) => (
                <button key={e} onClick={() => { reagujKomentar(p, k, e); setKomEmojiOpen(null); }}
                  className="text-lg w-8 h-8 rounded hover:bg-slate-100">{e}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Fotka: zmenšit na max 1000 px, JPEG, cíl pod 400 KB (limit serveru,
  // mirror klientské aplikace).
  const nactiFotku = async (file) => {
    if (!file) return;
    let src = file;
    if (jeHeic(file)) {
      setFotoBusy(true); setErr("");
      try {
        const heic2any = await loadHeic2any();
        const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
        src = Array.isArray(out) ? out[0] : out; // Blob (JPEG)
      } catch (e) {
        setFotoBusy(false);
        setErr("HEIC fotku se nepodařilo zpracovat, zkuste ji uložit jako JPG.");
        return;
      }
    } else if (!(file.type || "").startsWith("image/")) {
      setErr("Vyberte prosím obrázek.");
      return;
    }
    setFotoBusy(true); setErr("");
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let maxPx = 1000, q = 0.85, url = "";
        for (let pokus = 0; pokus < 4; pokus++) {
          const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
          const c = document.createElement("canvas");
          c.width = Math.round(img.width * scale);
          c.height = Math.round(img.height * scale);
          c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
          url = c.toDataURL("image/jpeg", q);
          if (url.length <= 390 * 1024) break;
          maxPx = Math.round(maxPx * 0.75); q = Math.max(0.6, q - 0.1);
        }
        if (url.length > 400 * 1024) { setFotoBusy(false); setErr("Fotka je moc velká, zkuste jinou"); return; }
        setNovaFotka(url);
        setFotoBusy(false);
      };
      img.onerror = () => { setFotoBusy(false); setErr("Fotku se nepodařilo načíst"); };
      img.src = reader.result;
    };
    reader.onerror = () => { setFotoBusy(false); setErr("Fotku se nepodařilo načíst"); };
    reader.readAsDataURL(src);
  };

  const napsat = async () => {
    const text = novy.trim();
    if (text.length < 3 && !novaFotka) { setErr("Napište prosím aspoň pár slov"); return; }
    setBusy(true);
    const obrazek = novaFotka || null;
    const tmpId = "tmp-" + Date.now();
    // Optimisticky: příspěvek se ukáže HNED (za připnutými), fotka přihlášeného taky.
    const optimista = {
      id: tmpId, autor: user?.name || "Já", tym: true, pinned: false, foto: mojeFoto,
      text, obrazek, cas: new Date().toISOString(), auto: null,
      reakce: {}, mojeReakce: [], komentare: [], moje: true, optimista: true,
    };
    setPosts((arr) => {
      const a = arr || [];
      const pinned = a.filter((x) => x.pinned).length;
      return [...a.slice(0, pinned), optimista, ...a.slice(pinned)];
    });
    setNovy(""); setNovaFotka(null);
    const j = await post({ action: "post", text, obrazek: obrazek || undefined });
    if (j && j.id) {
      setPosts((arr) => (arr || []).map((x) => (x.id === tmpId ? { ...x, id: j.id, optimista: false } : x)));
      load(0); // dosync na pozadí (bez blokování UI)
    } else {
      setPosts((arr) => (arr || []).filter((x) => x.id !== tmpId)); // selhalo → odeber
    }
    setBusy(false);
  };

  // Připnout/odepnout příspěvek nahoru (owner + CSM) — např. výzva týdne.
  const pripnout = async (p) => {
    if (await post({ action: "pin", prispevek_id: p.id, pinned: !p.pinned })) await load(0);
  };

  const skryt = (p) => setConfirmState({
    title: "Skrýt příspěvek z deníku?",
    description: <>Příspěvek „{String(p.text || "").slice(0, 80)}…" zmizí z feedu klientkám i týmu.</>,
    confirmLabel: "Skrýt",
    onConfirm: async () => {
      setConfirmState(null);
      if (await post({ action: "hide", prispevek_id: p.id })) await load(0);
    },
  });

  // Trvale smazat příspěvek (owner/manager) — i s komentáři a reakcemi.
  const smazatPrispevek = (p) => setConfirmState({
    title: "Trvale smazat příspěvek?",
    description: <>Příspěvek „{String(p.text || "").slice(0, 80)}…" se <b>trvale smaže</b> i s komentáři a reakcemi. Nelze vrátit.</>,
    confirmLabel: "Smazat natrvalo",
    onConfirm: async () => {
      setConfirmState(null);
      if (await post({ action: "post-delete", prispevek_id: p.id })) await load(0);
    },
  });

  // Skrýt/archivovat komentář (owner/manager/CSM) — zmizí z feedu.
  const skrytKomentar = (p, k) => {
    if (String(k.id || "").startsWith("tmp-")) return;
    setConfirmState({
      title: "Skrýt tento komentář?",
      description: "Komentář zmizí z feedu klientkám i týmu.",
      confirmLabel: "Skrýt",
      onConfirm: async () => {
        setConfirmState(null);
        setPosts((arr) => (arr || []).map((x) => x.id !== p.id ? x
          : { ...x, komentare: (x.komentare || []).filter((c) => c.id !== k.id) }));
        const j = await post({ action: "comment-hide", komentar_id: k.id });
        if (!j) load(0);
      },
    });
  };

  return (
    <div className={`${vTrenerovi ? "space-y-4" : "p-4 sm:p-6 lg:p-8 space-y-5"} max-w-4xl mx-auto w-full min-w-0`}>
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title || ""}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel || "Potvrdit"}
        variant="danger"
        onConfirm={() => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) fn(); }}
        onCancel={() => setConfirmState(null)}
      />

      {/* Hlavička: na mobilu jsou Zpět a Obnovit tlačítka na prst (44 px, orámovaná),
          na počítači drobné ikonky bez rámu, stejný vzor jako .btn-mesic v PM aplikaci. */}
      <div className="flex items-center gap-2">
        {vTrenerovi && <button onClick={zpet} className={IKONA_TLACITKO} aria-label="Zpět" title="Zpět"><ArrowLeft className="w-5 h-5 sm:w-4 sm:h-4" /></button>}
        <Trophy className={`${vTrenerovi ? "w-6 h-6" : "w-7 h-7"} text-amber-500 flex-shrink-0`} />
        <h1 className={`${vTrenerovi ? "text-xl" : "text-2xl"} font-bold text-slate-800 truncate`}>Deník úspěchů</h1>
        {celkem > 0 && <span className="text-xs font-semibold text-slate-400 bg-slate-100 rounded-full px-2.5 py-1">{celkem}</span>}
        <button onClick={() => load(0)} className={`${IKONA_TLACITKO} ml-auto`} aria-label="Obnovit" title="Obnovit"><RefreshCw className="w-5 h-5 sm:w-4 sm:h-4" /></button>
      </div>
      <div className={`text-sm text-slate-500 ${vTrenerovi ? "hidden sm:block" : ""}`}>
        Feed z klientské aplikace. Vaše komentáře klientky uvidí se štítkem TRENÉR, příspěvky se štítkem FITWAY40, pište proto klientkám a vykejte jim.
      </div>
      {vTrenerovi && <div className="sm:hidden text-xs text-slate-500 -mt-2">Klientky vidí vaše komentáře se štítkem TRENÉR. Pište jim a vykejte.</div>}

      {/* Nový příspěvek týmu */}
      <div className="glass min-w-0 space-y-3 p-4 sm:p-5">
        <div className="text-sm font-bold text-slate-900">Napsat do deníku, uvidí to všechny klientky</div>
        {emojiOpen && (
          <div className="flex flex-wrap gap-1 p-2 border border-slate-200 rounded-xl bg-slate-50">
            {EMOJI_PICKER.map((e) => (
              <button key={e} type="button" onClick={() => setNovy((t) => (t + e).slice(0, 400))}
                className="text-xl w-9 h-9 rounded-lg hover:bg-white transition-colors">{e}</button>
            ))}
          </div>
        )}
        <textarea value={novy} onChange={(e) => setNovy(e.target.value)} rows={5} maxLength={400}
          placeholder="Napište příspěvek klientkám. Uvidí ho se štítkem FitWay40."
          className="field min-h-[140px] text-[15px] leading-relaxed" />
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => fotkaRef.current?.click()} title="Přidat fotku, zvládne i HEIC z iPhonu" disabled={fotoBusy}
            className="btn btn-secondary">
            <ImageIcon className="h-[18px] w-[18px]" /> {fotoBusy ? "Zpracovávám" : "Fotka"}
          </button>
          <input ref={fotkaRef} type="file" accept="image/*,.heic,.heif" className="hidden"
            onChange={(e) => { nactiFotku(e.target.files?.[0]); e.target.value = ""; }} />
          <button type="button" onClick={() => setEmojiOpen((o) => !o)} title="Vložit emoji"
            className="btn btn-secondary">
            <span className="text-base leading-none">😊</span> Emoji
          </button>
        </div>
        {novaFotka && (
          <div className="relative inline-block">
            <img src={novaFotka} alt="" className="max-h-40 rounded-xl border border-slate-200" />
            <button type="button" onClick={() => setNovaFotka(null)} title="Odebrat fotku"
              className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-1 shadow text-slate-500 hover:text-rose-600">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <button onClick={napsat} disabled={busy || (novy.trim().length < 3 && !novaFotka)}
            className="btn btn-primary">
            <Send className="w-4 h-4" /> Zveřejnit
          </button>
          <span className="text-xs text-slate-400">{novy.length}/400, zveřejní se jako {user?.name || "vy"}, FitWay40</span>
        </div>
      </div>

      {err && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5">{err}</div>}

      {/* Feed */}
      {posts === null ? (
        <div className="text-sm text-slate-400">Načítám deník…</div>
      ) : posts.length === 0 ? (
        <div className="text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">Zatím žádné příspěvky.</div>
      ) : posts.map((p) => (
        <div key={p.id} className="glass min-w-0 space-y-3.5 p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <Avatar foto={p.foto} jmeno={p.autor} tym={p.tym} size={52} />
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                {p.autor}
                {p.pinned && <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-300 rounded-lg px-2 py-0.5">📌 PŘIPNUTO</span>}
                {p.tym && <span className="text-[11px] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-2 py-0.5">FITWAY40</span>}
                {p.auto && <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-0.5">🏅 ÚSPĚCH</span>}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">{fmtDT(p.cas)}{p.auto ? ", automaticky" : ""}</div>
            </div>
            {canPin && (
              <button onClick={() => pripnout(p)} title={p.pinned ? "Odepnout z vrchu feedu" : "Připnout na vrch feedu (např. výzva)"}
                className={`p-1 ${p.pinned ? "text-amber-500 hover:text-amber-600" : "text-slate-300 hover:text-amber-500"}`}>
                {p.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
              </button>
            )}
            {canHideMod && (
              <button onClick={() => skryt(p)} title="Skrýt / archivovat příspěvek (moderace)"
                className="text-slate-300 hover:text-amber-500 p-1"><EyeOff className="w-4 h-4" /></button>
            )}
            {canDeleteMod && (
              <button onClick={() => smazatPrispevek(p)} title="Trvale smazat příspěvek (moderace)"
                className="text-slate-300 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
          <div className="text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{fmtText(p.text)}</div>
          {p.obrazek && <img src={p.obrazek} alt="" className="max-h-96 rounded-xl object-cover" />}

          {/* Reakce */}
          <div className="flex items-center gap-1.5">
            {EMOJI.map((e) => {
              const n = p.reakce?.[e] || 0;
              const moje = (p.mojeReakce || []).includes(e);
              return (
                <button key={e} onClick={() => reaguj(p, e)}
                  className={`text-base rounded-full px-3.5 py-1.5 border transition-colors ${moje ? "bg-emerald-50 border-emerald-300 font-semibold" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`}>
                  {e}{n ? ` ${n}` : ""}
                </button>
              );
            })}
          </div>

          {/* Komentáře — ploché (chronologicky), bez vláken/odpovědí */}
          {(p.komentare || []).length > 0 && (
            <div className="space-y-2.5 border-t border-slate-100 pt-3">
              {(p.komentare || []).map((k) => renderComment(p, k, false))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input value={komText[p.id] || ""} onChange={(e) => setKomText((s) => ({ ...s, [p.id]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") komentuj(p); }}
              placeholder="Napsat komentář (klientka uvidí se štítkem TRENÉR)…" maxLength={300}
              className="field flex-1" />
            <button onClick={() => komentuj(p)} disabled={!String(komText[p.id] || "").trim()}
              className="text-sm font-semibold text-sky-700 hover:text-sky-900 disabled:opacity-40 px-2">Odeslat</button>
          </div>
        </div>
      ))}

      {posts !== null && posts.length < celkem && (
        <button onClick={() => load(posts.length)}
          className="w-full text-sm font-semibold text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl py-3 bg-white">
          Načíst starší ({celkem - posts.length})
        </button>
      )}
    </div>
  );
}
